import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts';
import jsPDF from 'jspdf';
import { Dropdown } from 'primereact/dropdown';

import { exportTimestamp, exportedOnText } from '../../export/stamp';
import tnDistrictsSvg from '../../json/tnDistrictsSvg.json';
import './DistrictMap.scss';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface MapMetricOption {
  label: string;
  value: string;
}

export interface DistrictMapProps {
  /** Card title (e.g. "Total Students"). */
  title?: string;
  /** Metric dropdown options. */
  metrics?: MapMetricOption[];
  /** Selected metric value. */
  metric?: string;
  onMetricChange?: (value: string) => void;
  /** district_id → value map for the currently selected metric. */
  data: Record<string, number>;
  /** Unit suffix shown on the legend max (e.g. "L"). */
  unitSuffix?: string;
  /** Close handler for the ✕ button. */
  onClose?: () => void;
  /**
   * Fired when a district is clicked. Receives the clicked district NAME (as
   * shown on the map, UPPERCASE). The dashboard resolves it to the master
   * district and toggles the selection — name-based so it never depends on the
   * SVG id matching the master district_id.
   */
  onDistrictClick?: (districtName: string) => void;
  /**
   * Name of the currently-selected district (from the header/drill). The map
   * highlights it and clears the highlight when it becomes null — so the map
   * stays in sync with the rest of the dashboard. Matched case-insensitively.
   */
  selectedDistrictName?: string | null;
  /** Card height in px. Defaults to 560. */
  height?: number;
  className?: string;
}

const MAP_NAME = 'tamilnadu';
let registered = false;

// Reference TN geometry: 38 districts as SVG paths (includes Mayiladuthurai,
// which the old GeoJSON lacked). Registered with ECharts as an SVG map.
const SVG_PATHS = tnDistrictsSvg as {
  id: string;
  name: string;
  path: string;
}[];
const MAP_SVG =
  `<svg viewBox="1000 1100 6000 7000" xmlns="http://www.w3.org/2000/svg">` +
  `<g transform="matrix(1 0 0 -1 0 9200)">` +
  SVG_PATHS.map((p) => `<path name="${p.name}" d="${p.path}"></path>`).join(
    ''
  ) +
  `</g></svg>`;

// Districts come from different sources (API uppercase vs master title-case),
// so match on a normalized key.
const norm = (sVal: string) => String(sVal).trim().toUpperCase();
// Format a legend value the same way the max label does: with a unit suffix
// (e.g. "2.6 L") when one is given, otherwise a plain grouped number.
const formatLegendValue = (value: number, unitSuffix: string): string =>
  unitSuffix
    ? `${(value / 100000).toFixed(1)}${unitSuffix}`
    : Math.round(value).toLocaleString('en-IN');

// Region names in the SVG are UPPERCASE; show them title-cased like before.
const toTitle = (sVal: string) =>
  sVal.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// Low→High choropleth gradient. Shared by the visualMap legend AND the
// per-district fill so the two always match.
const GRADIENT_STOPS = ['#e7f5e9', '#a5d6a7', '#66bb6a', '#2e9e4f', '#1b7a34'];
// Colour used to highlight the currently-selected district.
const SELECTED_AREA_COLOR = '#f4b740';

const hexToRgb = (hex: string) => {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

// Map a 0..1 position along the Low→High gradient to an interpolated colour.
const colorAtPosition = (t: number): string => {
  const clamped = Math.min(1, Math.max(0, t));
  const pos = clamped * (GRADIENT_STOPS.length - 1);
  const i = Math.min(GRADIENT_STOPS.length - 2, Math.floor(pos));
  const frac = pos - i;
  const a = hexToRgb(GRADIENT_STOPS[i]);
  const b = hexToRgb(GRADIENT_STOPS[i + 1]);
  const mix = (x: number, y: number) => Math.round(x + (y - x) * frac);
  return `rgb(${mix(a.r, b.r)}, ${mix(a.g, b.g)}, ${mix(a.b, b.b)})`;
};

// A district's gradient fill. A plain value/max linear scale collapses the map
// to one pale green when a single metro district (e.g. Chennai) dwarfs every
// other value — all the non-max districts land near the bottom stop. Instead we
// colour by RANK (quantile): districts are ordered by value and spread evenly
// across the Low→High gradient, so the choropleth actually distinguishes high
// vs low districts regardless of outliers. Equal values share a rank, and a
// value of 0 (no data / unmatched) always stays at the lowest stop.
//   sortedValues — all district values ascending (0s included)
//   value        — this district's value
const gradientAreaColor = (value: number, sortedValues: number[]): string => {
  if (value <= 0 || sortedValues.length <= 1) return GRADIENT_STOPS[0];
  // Position = fraction of districts with a strictly smaller value (rank), so
  // the ordering drives the colour, not the raw magnitude.
  let below = 0;
  for (let k = 0; k < sortedValues.length; k += 1) {
    if (sortedValues[k] < value) below += 1;
    else break;
  }
  const t = below / (sortedValues.length - 1);
  return colorAtPosition(t);
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

/**
 * Tamil Nadu district choropleth (ECharts): High→Low green legend, pan/zoom,
 * tooltip, and click-to-drill-down. Driven by a { district: value } map.
 */
const DistrictMap: React.FC<DistrictMapProps> = ({
  title = 'Total Students',
  metrics = [],
  metric,
  onMetricChange,
  data,
  unitSuffix = '',
  onClose,
  onDistrictClick,
  selectedDistrictName = null,
  height = 560,
  className = '',
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  // Legend range slider: districts whose value is BELOW `threshold` are dimmed
  // on the map so the user can highlight districts by count (drag the handle).
  // 0 = show everything (default). Kept as component state so it drives both
  // the slider UI and the per-district dimming in `seriesData`.
  const [threshold, setThreshold] = useState(0);

  // Register the GeoJSON once.
  useEffect(() => {
    if (!registered) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      echarts.registerMap(MAP_NAME, { svg: MAP_SVG } as any);
      registered = true;
    }
  }, []);

  const maxValue = useMemo(() => Math.max(1, ...Object.values(data)), [data]);

  const seriesData = useMemo(() => {
    // `data` is keyed by district_id (matches each SVG polygon's `id`), so the
    // value lands on the right district regardless of name spelling.
    const sel = selectedDistrictName ? norm(selectedDistrictName) : null;
    // All district values, ascending — drives the RANK-based gradient so one
    // outlier metro doesn't flatten every other district to the palest stop.
    const sortedValues = SVG_PATHS.map((p) => data[p.id] ?? 0).sort(
      (a, b) => a - b
    );
    // Half-width of the highlight band around the slider position. The slider
    // picks a value on the scale and only districts WITHIN this band of it are
    // highlighted (a moving window), not everything above it. 6% of the full
    // range keeps the band narrow enough to isolate a small cluster.
    const band = maxValue * 0.06;
    return SVG_PATHS.map((p) => {
      const value = data[p.id] ?? 0;
      // Option B — the slider band and the drill-down selection share ONE
      // orange and are MUTUALLY EXCLUSIVE, decided by whether the slider is in
      // use (threshold > 0):
      //   • slider active  → only the band districts are orange; the selection
      //                      highlight yields to it.
      //   • slider at 0    → only the selected district is orange (if any).
      // The reverse guard lives in the reset effect: selecting a district pushes
      // the slider back to 0, so the two highlight sets never appear together.
      const sliderActive = threshold > 0;
      const inRange =
        sliderActive && value > 0 && Math.abs(value - threshold) <= band;
      const isSelected = !sliderActive && sel != null && norm(p.name) === sel;
      // EVERY district carries its explicit areaColor:
      //  - selected OR in the slider band → highlight orange (same colour)
      //  - otherwise                      → its green gradient colour
      // Because the colour is stored explicitly, lowering the slider or
      // deselecting simply swaps orange back to the gradient colour — no stale
      // highlight lingers after setOption re-applies the data.
      return {
        name: p.name,
        value,
        itemStyle: {
          areaColor:
            isSelected || inRange
              ? SELECTED_AREA_COLOR
              : gradientAreaColor(value, sortedValues),
        },
      };
    });
  }, [data, selectedDistrictName, threshold, maxValue]);

  // The click listener is bound once at mount, so keep the latest click
  // callback in a ref — otherwise the bound closure captures the FIRST render's
  // handler (built before the district master list loaded) and clicks silently
  // resolve to nothing.
  const onDistrictClickRef = useRef(onDistrictClick);
  useEffect(() => {
    onDistrictClickRef.current = onDistrictClick;
  }, [onDistrictClick]);

  // Reset the range slider to 0 whenever the dataset changes (new metric /
  // drill-down swaps the value scale) OR a district becomes selected. The
  // selection reset enforces Option B — the slider and the drill-down highlight
  // are mutually exclusive, so selecting a district clears any active band and
  // leaves only the selected district orange.
  useEffect(() => {
    setThreshold(0);
  }, [maxValue, selectedDistrictName]);

  // Init chart.
  useEffect(() => {
    if (!elRef.current) return;
    const chart = echarts.init(elRef.current);
    chartRef.current = chart;

    chart.on('click', (params) => {
      const nm = params.name as string | undefined;
      if (!nm) return;
      // Emit the clicked district NAME. The dashboard resolves it to the master
      // district (normalized) and toggles the selection, so a click selects a
      // new district or clears it if it was already selected.
      onDistrictClickRef.current?.(nm);
    });

    const onResize = () => chart.resize();
    window.addEventListener('resize', onResize);

    // Observe the container itself so the chart resizes whenever its box
    // changes size — not just on window resize. This fixes the map rendering
    // empty on mobile / when the panel opens, where ECharts initialises before
    // the container has a measurable width.
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && elRef.current) {
      ro = new ResizeObserver(() => chart.resize());
      ro.observe(elRef.current);
    }
    // Also resize on the next frame, after the initial layout settles.
    const raf = requestAnimationFrame(() => chart.resize());

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
      ro?.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update option when data/metric changes.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.setOption(
      {
        tooltip: {
          trigger: 'item',
          formatter: (p: { name?: string; value?: number }) => {
            const v = Number.isFinite(p.value) ? p.value : 0;
            return `<b>${p.name}</b><br/>${title}: ${Number(v).toLocaleString(
              'en-IN'
            )}`;
          },
        },
        // NO visualMap: it owns each region's areaColor from the value and
        // would override the selected district's gold fill (the fill only
        // showed on hover/emphasis then reverted — the "not staying selected"
        // bug). Every district is coloured explicitly in `seriesData`
        // (gradientAreaColor, or gold when selected), and the High→Low legend
        // is rendered as a plain gradient bar in the JSX below.
        series: [
          {
            name: title,
            type: 'map',
            map: MAP_NAME,
            // Only ONE district highlighted at a time — clicking another clears
            // the previous selection (otherwise ECharts keeps every clicked
            // region shaded gold, so two districts look selected at once).
            // No persistent "selected" shading — a map click only re-scopes the
            // dashboard (handled in onClick), it must not leave a gold region
            // behind. Disabling selection stops two districts looking selected
            // at once (previous click + new click).
            selectedMode: false,
            // Fixed in place: no free-drag/scroll-zoom roaming. The map stays at
            // its default size/position; zoom is still available via the explicit
            // +/−/reset buttons (which call setOption zoom), so users aren't stuck
            // but the map can't be accidentally dragged off-centre.
            roam: false,
            layoutCenter: ['50%', '50%'],
            layoutSize: '96%',
            scaleLimit: { min: 1, max: 8 },
            label: {
              show: true,
              fontSize: 8.5,
              color: '#5b6470',
              formatter: (p: { name?: string }) => toTitle(p.name ?? ''),
            },
            // Hover-only highlight (no persistent selection). Gold on hover so
            // the user sees which district they are about to click; it clears
            // as soon as the cursor leaves, so no district stays shaded.
            emphasis: {
              label: {
                show: true,
                fontWeight: 700,
                color: '#111827',
                formatter: (p: { name?: string }) => toTitle(p.name ?? ''),
              },
              itemStyle: { areaColor: '#f4b740' },
            },
            itemStyle: {
              borderColor: '#ffffff',
              borderWidth: 1,
            },
            data: seriesData,
          },
        ],
      },
      // Replace the whole series each update (don't merge). ECharts' default
      // merge kept a district's previously-set gold fill, so deselecting it from
      // the dropdown left it gold instead of restoring its gradient colour.
      // replaceMerge rebuilds the series from the new data every time, so the
      // stored gradient colour is reapplied cleanly on deselect.
      { replaceMerge: ['series'] }
    );
    chart.resize();
  }, [seriesData, maxValue, title, unitSuffix]);

  // (The selected district highlight is baked into `seriesData` above and
  // applied by the setOption effect — no dispatchAction needed, which is
  // unreliable on an SVG-registered map.)

  // Toolbar handlers (zoom via ECharts roam state).
  const zoom = (factor: number) => {
    const chart = chartRef.current;
    if (!chart) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const opt: any = chart.getOption();
    const cur = opt?.series?.[0]?.zoom ?? 1;
    chart.setOption({ series: [{ zoom: cur * factor }] });
  };
  const resetZoom = () => {
    chartRef.current?.setOption({
      series: [{ zoom: 1, center: null }],
    });
  };

  // Download modal (PDF / PNG / JPG) — mirrors AppChart's chart-download popup
  // so the map export matches the chart experience. The stamp is frozen when
  // the dialog opens so the previewed filename equals the saved one.
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadStamp, setDownloadStamp] = useState('');
  const stamp = () => downloadStamp || exportTimestamp();
  const exportBaseName = () => `${title.replace(/\s+/g, '_')}_map_${stamp()}`;

  const openDownload = () => {
    setDownloadStamp(exportTimestamp());
    setDownloadOpen(true);
  };

  // Rasterize the current map view and trigger a download in the chosen format.
  const downloadImage = (format: 'png' | 'jpg') => {
    const chart = chartRef.current;
    if (!chart) return;
    const url = chart.getDataURL({
      type: format === 'jpg' ? 'jpeg' : 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportBaseName()}.${format}`;
    a.click();
    setDownloadOpen(false);
  };

  const downloadPdf = () => {
    const chart = chartRef.current;
    if (!chart) return;
    const dataUrl = chart.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });
    // Size the PDF to the rendered image; add a title + "Exported on" caption.
    const img = new Image();
    img.onload = () => {
      const topOffset = title ? 66 : 48;
      const pdf = new jsPDF({
        orientation: img.width >= img.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [img.width + 40, img.height + topOffset + 28],
      });
      if (title) {
        pdf.setFontSize(16);
        pdf.setTextColor(31, 41, 55);
        pdf.text(title, 20, 36);
      }
      pdf.setFontSize(10);
      pdf.setTextColor(120);
      pdf.text(exportedOnText(), 20, title ? 54 : 32);
      pdf.addImage(dataUrl, 'PNG', 20, topOffset, img.width, img.height);
      pdf.save(`${exportBaseName()}.pdf`);
      setDownloadOpen(false);
    };
    img.src = dataUrl;
  };

  return (
    <div className={`district-map ${className}`.trim()}>
      <div className="district-map__head">
        <span className="district-map__title-wrap">
          <i className="pi pi-map district-map__icon" />
          <h6 className="district-map__title">{title}</h6>
        </span>
        {onClose && (
          <button
            type="button"
            className="district-map__close"
            aria-label="Close map"
            onClick={onClose}
          >
            <i className="pi pi-times" />
          </button>
        )}
      </div>

      <div className="district-map__controls">
        {metrics.length > 0 && (
          <Dropdown
            value={metric}
            options={metrics}
            onChange={(e) => onMetricChange?.(e.value)}
            className="district-map__metric"
            panelClassName="district-map__metric-panel"
          />
        )}
        <button
          type="button"
          className="district-map__download"
          aria-label="Download map"
          onClick={openDownload}
        >
          <i className="pi pi-download" />
        </button>
      </div>

      <div className="district-map__canvas-wrap" style={{ height }}>
        <div ref={elRef} className="district-map__canvas" />

        {/* High→Low gradient legend with an interactive range slider: drag the
            handle to a count and only districts at/above it stay highlighted
            (the rest dim). The slider only drives `threshold` → opacity in
            `seriesData`; it never owns areaColor, so the selected-district gold
            fill and drill-down are unaffected. */}
        <div className="district-map__legend">
          <span className="district-map__legend-label" aria-hidden="true">
            High
            <span className="district-map__legend-value">
              {formatLegendValue(maxValue, unitSuffix)}
            </span>
          </span>

          <div className="district-map__legend-track">
            <span
              className="district-map__legend-bar"
              aria-hidden="true"
              style={{
                background: `linear-gradient(to top, ${GRADIENT_STOPS.join(
                  ', '
                )})`,
              }}
            />
            {/* Vertical range input overlaid on the bar. Rotated via CSS so 0
                sits at the bottom and max at the top, matching the gradient. */}
            <input
              type="range"
              className="district-map__legend-slider"
              min={0}
              max={maxValue}
              step={Math.max(1, Math.round(maxValue / 100))}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              aria-label="Highlight districts at or above this value"
            />
            {threshold > 0 && (
              <span
                className="district-map__legend-threshold"
                style={{ bottom: `${(threshold / maxValue) * 100}%` }}
              >
                = {formatLegendValue(threshold, unitSuffix)}
              </span>
            )}
          </div>

          <span className="district-map__legend-label" aria-hidden="true">
            Low<span className="district-map__legend-value">0</span>
          </span>
        </div>

        <div className="district-map__zoom">
          <button type="button" aria-label="Zoom in" onClick={() => zoom(1.4)}>
            <i className="pi pi-search-plus" />
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            onClick={() => zoom(1 / 1.4)}
          >
            <i className="pi pi-search-minus" />
          </button>
          <button type="button" aria-label="Reset" onClick={resetZoom}>
            <i className="pi pi-refresh" />
          </button>
        </div>
      </div>

      <p className="district-map__hint">
        <span aria-hidden>👆</span> Click a district to drill down · use the +/−
        buttons to zoom
      </p>

      {/* ── Download modal — same look & options as AppChart's download popup
          (reuses the global `.chart-download` classes). Three formats:
          PDF / PNG / JPG. ── */}
      {downloadOpen && (
        <div
          className="chart-download"
          role="dialog"
          aria-modal="true"
          aria-label="Download map"
          onClick={() => setDownloadOpen(false)}
        >
          <div
            className="chart-download__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="chart-download__header">
              <span className="chart-download__header-title">
                <i className="pi pi-download" />
                Download Map
              </span>
              <button
                type="button"
                className="chart-download__close"
                aria-label="Close"
                onClick={() => setDownloadOpen(false)}
              >
                <i className="pi pi-times" />
              </button>
            </div>

            <div className="chart-download__body">
              <p className="chart-download__file-label">FILE NAME</p>
              <p className="chart-download__file-name">{exportBaseName()}</p>
              <p className="chart-download__file-note">
                <i className="pi pi-clock" /> {exportedOnText()}
              </p>

              <div className="chart-download__options">
                <button
                  type="button"
                  className="chart-download__option chart-download__option--pdf"
                  onClick={downloadPdf}
                >
                  <span className="chart-download__badge chart-download__badge--pdf">
                    <i className="pi pi-file-pdf" />
                  </span>
                  <span className="chart-download__type">PDF</span>
                  <span className="chart-download__ext">.pdf</span>
                </button>

                <button
                  type="button"
                  className="chart-download__option chart-download__option--png"
                  onClick={() => downloadImage('png')}
                >
                  <span className="chart-download__badge chart-download__badge--png">
                    <i className="pi pi-image" />
                  </span>
                  <span className="chart-download__type">PNG</span>
                  <span className="chart-download__ext">.png</span>
                </button>

                <button
                  type="button"
                  className="chart-download__option chart-download__option--jpg"
                  onClick={() => downloadImage('jpg')}
                >
                  <span className="chart-download__badge chart-download__badge--jpg">
                    <i className="pi pi-image" />
                  </span>
                  <span className="chart-download__type">JPG</span>
                  <span className="chart-download__ext">.jpg</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistrictMap;
